// apps/client/src/game/interactions/plant.ts (M1-T15) — with a fruit selected in Pockets, A plants it on the grass tile in front
// (the same tile rules the server checks: rules/fruit.canPlantAt plus the free-tile and tree-spacing checks of fruit.plant).
import { getEntry, tuning } from '@whiskerton/content';
import { inRect, rotateFootprint, type ItemId, type MapData, type TileXY, type WorldObject } from '@whiskerton/shared';
import { canPlantAt } from '@whiskerton/shared/rules/fruit';
import type { Interaction, InteractCtx } from '../interact';
import { objectsOf } from '../../net/handlers/nature';
import { useGame } from '../../state/store';
import { itemsLayerOf } from '../../world/items';

const same = (a: TileXY, b: TileXY) => a.x === b.x && a.y === b.y;
function covers(o: WorldObject, t: TileXY): boolean {
  const e = getEntry(o.catalogId);
  const { w, d } = rotateFootprint(e && e.kind === 'world' ? e.footprint : [1, 1], o.rot);
  return t.x >= o.x && t.x < o.x + w && t.y >= o.y && t.y < o.y + d;
}
/** Internal (beyond the M1-T15 contract; exported for tests). Mirrors the server's plantBlocked: an object on the tile, a tree beside it, a dropped item, a house, door, mailbox or site. */
export function plantBlocked(ctx: InteractCtx, map: MapData, t: TileXY): boolean {
  const objs = objectsOf(ctx.game);
  if (objs.some((o) => covers(o, t))) return true;
  if (objs.some((o) => o.kind === 'tree' && Math.max(Math.abs(o.x - t.x), Math.abs(o.y - t.y)) === 1)) return true;
  if (itemsLayerOf(ctx.game)?.occupied(t)) return true;
  if (map.plots.some((p) => inRect(p.house, t.x, t.y) || same(p.door, t) || same(p.mailbox, t))) return true;
  const s = map.sites;
  if ([s.plaza, s.shop, s.museum].some((r) => inRect(r, t.x, t.y)) || [s.board, s.dropBox, s.shopDoor, s.museumDoor].some((p) => same(p, t))) return true;
  return !(ctx.game.scenes.current?.walkable({ x: t.x + 0.5, y: t.y + 0.5 }) ?? true);
}
const frontTile = (ctx: InteractCtx): TileXY => ({ x: Math.floor(ctx.pos.x + Math.sin(ctx.f) * 0.9), y: Math.floor(ctx.pos.y + Math.cos(ctx.f) * 0.9) });

export const interaction: Interaction = {
  id: 'plant',
  find(ctx) {
    const s = ctx.store, map = s.map;
    if (ctx.space !== 'out' || !map || !s.you || !s.inventorySelected) return null;
    const item = s.you.items.find((i) => i.id === s.inventorySelected);
    if (!item || item.at.loc !== 'pockets' || getEntry(item.catalogId)?.kind !== 'fruit') return null;
    const t = frontTile(ctx);
    if (same(t, { x: Math.floor(ctx.pos.x), y: Math.floor(ctx.pos.y) })) return null;
    const c = { x: t.x + 0.5, y: t.y + 0.5 };
    if (Math.hypot(c.x - ctx.pos.x, c.y - ctx.pos.y) > tuning.town.interactRange) return null;
    if (!canPlantAt(map, t, (x) => plantBlocked(ctx, map, x))) return null;
    return { id: `${item.id}@${t.x},${t.y}`, at: c, verb: 'hud.verbPlant', priority: 45 };
  },
  run(ctx, c) {
    const [item, xy] = c.id.split('@') as [string, string];
    const [x, y] = xy.split(',').map(Number) as [number, number];
    ctx.game.you()?.avatar.play('plant');
    void ctx.game.cmd.act('fruit.plant', { item: item as ItemId, at: { x, y } }).then((res) => {
      const s = useGame.getState();
      if (res.ok && s.inventorySelected === item && !s.you?.items.some((i) => i.id === item)) s.inventorySelect(null);   // stack used up
    });
  },
};
